import { FormattedMessage, useIntl } from "react-intl";
import { Dialog } from "../../../../common/core-ui/Dialog.tsx";
import { CourseMaterial, useCoursesLoaderData } from "./coursesLoader.ts";
import useChangeContentDecision from "./useChangeContentDecision.tsx";
import { PrintInfo } from "common/api/decimal/methods/course.ts";
import useFindCourse from "./useFindCourse.tsx";
import MaterialMetaData from "./MaterialMetaData.tsx";
import styles from "./ConfirmOptInPrintDialog.module.scss";
import useToasts from "routes/root/core-ui/toast/useToasts.ts";
import ConfirmOptInPrintToast from "./ConfirmOptInPrintToast.tsx";
import { useRef } from "react";
import EmptyAssetCard from "common/features/asset/EmptyAssetCard.tsx";
import BookStoreHours from "./common/BookStoreHours.tsx";
import { optedOutWithoutBook, optedOutWithBook } from "./printStatusHelper.ts";

interface ConfirmOptInPrintDialogProps {
  onRequestClose: () => void;
  courseMaterial: CourseMaterial;
}

const Explanation = ({
  printInfo,
  courseMaterial: { printStatus, studentPrice, currency },
}: {
  printInfo: PrintInfo;
  courseMaterial: CourseMaterial;
}) => {
  const intl = useIntl();
  const price = intl.formatNumber(studentPrice ?? 0, {
    style: "currency",
    currency: currency ?? "USD",
  });

  return (
    <div>
      {optedOutWithoutBook(printStatus) && (
        <FormattedMessage
          defaultMessage={
            "Your student account will be charged {price} for this print book. Once you confirm, you can pick it up at the bookstore."
          }
          values={{ price }}
        />
      )}
      {optedOutWithBook(printStatus) && (
        <FormattedMessage
          defaultMessage={
            "To keep this book, your student account will be charged {price}. If you no longer want it, please return it to the bookstore by {date, date, long}."
          }
          values={{
            date: new Date(printInfo.returnDate),
            price,
          }}
        />
      )}
    </div>
  );
};

export default function ConfirmOptInPrintDialog({
  courseMaterial,
  onRequestClose,
}: ConfirmOptInPrintDialogProps) {
  const intl = useIntl();
  const { showToast } = useToasts();
  const { printStatus, courseId, vbid } = courseMaterial;
  const course = useFindCourse({ courseId })!;
  const { currentTerm } = useCoursesLoaderData();
  const printInfo = currentTerm!.printInfo!;
  const status = useRef(printStatus);
  const { contentOptIn, isOptDecisionProcessing } = useChangeContentDecision({
    courseMaterial,
    afterDecisionChange: () => {
      onRequestClose();
      showToast(
        <ConfirmOptInPrintToast
          vbid={vbid}
          previousPrintStatus={status.current}
          pickupDate={printInfo.pickupDate}
        />
      );
    },
  });

  if (
    !printStatus ||
    (!optedOutWithoutBook(printStatus) && !optedOutWithBook(printStatus))
  ) {
    return null;
  }

  const getTitle = () => {
    if (optedOutWithoutBook(printStatus)) {
      return intl.formatMessage({
        defaultMessage: "Print Book Confirmation & Pick Up",
      });
    } else if (optedOutWithBook(printStatus)) {
      return intl.formatMessage({
        defaultMessage: "Want to Keep Your Print Book?",
      });
    } else {
      return null;
    }
  };

  const getCancelLabel = () => {
    if (optedOutWithBook(printStatus)) {
      return intl.formatMessage({
        defaultMessage: "No, I'll Return It",
      });
    } else {
      return intl.formatMessage({
        defaultMessage: "Cancel",
      });
    }
  };

  const getConfirmLabel = () => {
    if (optedOutWithBook(printStatus)) {
      return intl.formatMessage({
        defaultMessage: "Yes, I'll Keep It",
      });
    } else {
      return intl.formatMessage({
        defaultMessage: "Confirm",
      });
    }
  };

  return (
    <Dialog
      canClose={!isOptDecisionProcessing}
      title={getTitle()}
      onRequestClose={onRequestClose}
      primaryButton={{
        onClick: () => void contentOptIn(),
        label: getConfirmLabel(),
        loading: isOptDecisionProcessing,
      }}
      secondaryButton={{
        onClick: onRequestClose,
        label: getCancelLabel(),
        disabled: isOptDecisionProcessing,
      }}
    >
      <div>
        <Explanation printInfo={printInfo} courseMaterial={courseMaterial} />
        <BookStoreHours printStatus={printStatus} printInfo={printInfo} />
        <h2 className={styles.courseTitle}>{course.title}</h2>
        <div className={styles.coverRow}>
          <EmptyAssetCard vbid={vbid} tagName={"div"} type={"horizontal"}>
            <div className={styles.metaData}>
              <MaterialMetaData
                titleTag="h3"
                courseMaterial={courseMaterial}
                showTags={false}
              />
            </div>
          </EmptyAssetCard>
        </div>
      </div>
    </Dialog>
  );
}
